import { GenreContent, GenreLevel } from '@app/_proto/Protos/genres';
import type { NovelItem } from '@app/_proto/Protos/novels';
import useGenres from '@app/domains/common/hooks/useGenres';
import HomePopularGenreNovels from '@app/domains/home/containers/HomePopularGenreNovels';
import useSearchNovels from '@app/domains/novel/hooks/useSearchNovels';
import { shuffle } from 'lodash-es';
import { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

export default function HomePopularGenreNovelsFetcher() {
    const { ref, inView } = useInView({
        triggerOnce: true,
    });

    const {
        genres,
        genreContents = [{ genreNovels: Array<undefined>(10).fill(undefined) }],
        isSuccess,
    } = useGenreNovel({ enabled: inView });

    return (
        <div ref={ref}>
            <HomePopularGenreNovels genres={genres} genreContents={genreContents} isSuccess={isSuccess} />
        </div>
    );
}

type UseGenreNovelParam = {
    enabled?: boolean;
};
type UseGenreNovelReturn = {
    genres: string[] | undefined;
    genreContents: { genre: string; genreNovels: NovelItem[] | undefined }[] | undefined;
    isSuccess: boolean;
};
const useGenreNovel = ({ enabled }: UseGenreNovelParam): UseGenreNovelReturn => {
    const { data: genres, isSuccess: isGenreSuccess } = useGenres({
        level: GenreLevel.MainGenreLevel,
        contentType: [GenreContent.NovelGenreContent],
        enabled,
    });

    const { data: novels, isSuccess } = useSearchNovels({
        genres: genres || ALL_GENRES_PARAM,
        enabled,
        count: AVERAGE_ITEM_COUNT * GENRE_COUNT,
    });

    const shuffledGenres = useMemo(() => (genres ? shuffle(genres) : undefined), [genres]);

    const genreContents = useMemo(() => {
        if (!novels) return undefined;
        const groupedContents = shuffledGenres?.map(genre => {
            const genreNovels = novels
                ?.filter(d => {
                    return d.genres.includes(genre);
                })
                .slice(0, 7);
            return { genre, genreNovels };
        });
        return groupedContents?.filter(contents => (contents?.genreNovels?.length || 0) >= MIN_GROUP_NOVEL_COUNT);
    }, [novels, shuffledGenres]);

    const availableGenres = genreContents?.map(contents => contents.genre);

    return { genres: availableGenres, genreContents, isSuccess: isGenreSuccess && isSuccess };
};

const AVERAGE_ITEM_COUNT = 15;
const GENRE_COUNT = 8;
const ALL_GENRES_PARAM = [];
const MIN_GROUP_NOVEL_COUNT = 4;
